import { useCallback, useEffect, useMemo, useState } from "react";
import { AddCommentCall, DeleteCommentCall, GetQuestionDetailsCall } from "../../redux/api/question.api";

const isOk = (res) => [200, 201, 204].includes(Number(res?.status));

const fullName = (u) => `${u?.first_name || ""} ${u?.last_name || ""}`.trim() || u?.email || "";

/** Users mentioned as `@Full Name` in `text`, among the question author and commenters. */
function findMentionIds(text, question, comments) {
  const lower = String(text || "").toLowerCase();
  const people = new Map();
  if (question?.user?.id) people.set(question.user.id, question.user);
  comments.forEach((c) => c?.user?.id && people.set(c.user.id, c.user));
  return [...people.values()]
    .filter((u) => {
      const name = fullName(u);
      return name && lower.includes(`@${name.toLowerCase()}`);
    })
    .map((u) => u.id);
}

/** Loads a question's comments and exposes add / remove with optimistic updates. */
export default function useQuestionThread(questionId) {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const comments = useMemo(() => (Array.isArray(question?.comments) ? question.comments : []), [question]);

  const reload = useCallback(async () => {
    if (!questionId) return;
    const res = await GetQuestionDetailsCall(questionId);
    const q = res?.question || res?.data || null;
    if (q) setQuestion(q);
  }, [questionId]);

  useEffect(() => {
    setQuestion(null);
    if (!questionId) return undefined;
    let alive = true;
    setLoading(true);
    GetQuestionDetailsCall(questionId).then((res) => {
      if (!alive) return;
      setQuestion(res?.question || res?.data || null);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [questionId]);

  const addComment = useCallback(
    async (text, me) => {
      const body = String(text || "").trim();
      if (!questionId || !body || posting) return false;
      const tempId = `temp-${Date.now()}`;
      const optimistic = { id: tempId, comment: body, user: me, createdAt: new Date().toISOString(), pending: true };
      setQuestion((q) => ({ ...(q || {}), comments: [...(q?.comments || []), optimistic] }));
      setPosting(true);
      const res = await AddCommentCall({
        question_id: questionId,
        comment: body,
        mentioned_users: findMentionIds(body, question, comments),
      });
      setPosting(false);
      if (!isOk(res)) {
        setQuestion((q) => ({ ...q, comments: (q?.comments || []).filter((c) => c.id !== tempId) }));
        return false;
      }
      await reload();
      return true;
    },
    [questionId, posting, question, comments, reload]
  );

  const removeComment = useCallback(
    async (commentId) => {
      const before = question;
      setQuestion((q) => ({ ...q, comments: (q?.comments || []).filter((c) => c.id !== commentId) }));
      const res = await DeleteCommentCall(commentId);
      if (!isOk(res)) setQuestion(before);
    },
    [question]
  );

  return { question, comments, loading, posting, addComment, removeComment };
}
